import { useEffect, useRef } from "react";

/**
 * V7.1 + V8.5.5: Re-executa `fn` quando o usuário volta para a aba/app
 * + (opcional) polling periódico via `intervalMs`.
 * Usa visibilitychange + window focus para máxima compatibilidade (PWA, mobile webview, etc.).
 *
 * Uso:
 *   useFocusRefresh(load, [user?.id]);
 *   useFocusRefresh(load, [], { intervalMs: 30000 });  // polling 30s
 *
 * @param {Function} fn - função a ser chamada
 * @param {Array} deps - dependências
 * @param {Object} opts - { minIntervalMs: 1500, intervalMs: 0 }
 */
export default function useFocusRefresh(fn, deps = [], opts = {}) {
  const { minIntervalMs = 1500, intervalMs = 0 } = opts;
  const lastRef = useRef(0);
  const fnRef = useRef(fn);

  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  useEffect(() => {
    const trigger = () => {
      const now = Date.now();
      if (now - lastRef.current < minIntervalMs) return;
      lastRef.current = now;
      try { fnRef.current?.(); } catch (e) { console.error("[useFocusRefresh]", e); }
    };
    const onVis = () => { if (document.visibilityState === "visible") trigger(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", trigger);
    // V8.5.5 — polling opcional
    const intervalHandle = intervalMs > 0 ? setInterval(trigger, intervalMs) : null;
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", trigger);
      if (intervalHandle) clearInterval(intervalHandle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
